import React from "react";
import {
  Grid,
  Typography,
  TextField as TF,
  Button,
  Autocomplete,
  CircularProgress,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { size, COLORS } from "../../utils";
import { DATA } from "./Data";
import styles from "./ReachUs.module.css";

const GOOGLE_SHEETS_WEB_APP_URL =
  "https://script.google.com/macros/s/AKfycbwhZeThLJxT6hNRaruUVSfKGYNNJm-YMfMEVarbpmdJ9ti15zg7nHyomYNYOfg2T-vCMQ/exec";

const API_URL = "http://api.thinkgrail.com/genesysemailapi/send-email";

const useStyles = makeStyles(() => ({
  cssOutlinedInput: {
    "&$cssFocused $notchedOutline": {
      borderColor: `${COLORS.C2} !important`,
    },
  },
  cssFocused: {
    borderColor: `${COLORS.C2} !important`,
  },
  notchedOutline: {
    borderWidth: size({ p: 0.08 }),
    borderColor: `${COLORS.C2} !important`,
  },
}));

function TextField({ label, tf_props, params }) {
  const cls = useStyles();
  params = params === undefined ? {} : params;

  return (
    <Grid item container direction={"column"} className={styles.fullwidthinput}>
      <Grid item>
        <Typography
          sx={{
            fontFamily: "Space Mono",
            fontSize: size({ p: 0.9 }),
            fontWeight: 400,
            marginBottom: size({ p: 0.5 }),
            color: `${COLORS.C2}E0`,
          }}
        >
          {label}
        </Typography>
      </Grid>
      <Grid item>
        <TF
          placeholder={label}
          {...tf_props}
          {...params}
          InputProps={{
            style: {
              fontFamily: "Space Mono",
              color: COLORS.C2,
              fontSize: size({ p: 0.9 }),
            },
            classes: {
              root: cls.cssOutlinedInput,
              focused: cls.cssFocused,
              notchedOutline: cls.notchedOutline,
            },
            ...params.InputProps,
          }}
          fullWidth
        />
      </Grid>
    </Grid>
  );
}

export default function ReachUs() {
  // --- Step 1: state for form data ---
  const [formData, setFormData] = React.useState({
    firstName: "",
    lastName: "",
    email: "",
    contact: "",
    service: "",
    subject: "",
    message: "",
  });

  const [isSubmitting, setIsSubmitting] = React.useState(false);

  // Build services list
  let SERVICES = Object.keys(DATA).map((k) => ({ label: k }));

  // Build backend email body
  const buildEmailBody = (data) => `
First Name: ${data.firstName}
Last Name: ${data.lastName}
Email: ${data.email}
Contact: ${data.contact}
Service: ${data.service}
Subject: ${data.subject}
Message: ${data.message}
`;

  // --- Step 4: submit handler ---
  const handleSubmit = async () => {
    if (!formData.email || !formData.firstName) {
      alert("Please fill in at least your First Name and Email");
      return;
    }

    setIsSubmitting(true);

    try {
      // Build payload for FastAPI backend
      const payload = {
        subject: "New Reach Us Submission",
        body: buildEmailBody(formData),
        base64_data: null,
        filename: null,
      };

      // Call FastAPI email endpoint
      await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      // Send to Google Sheets
      await fetch(GOOGLE_SHEETS_WEB_APP_URL, {
        method: "POST",
        mode: "no-cors",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      alert("Thank you for submitting the form!");

      // Reset form
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        contact: "",
        service: "",
        subject: "",
        message: "",
      });
    } catch (error) {
      alert("Oops, something went wrong. Please try again.");
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Grid
      item
      container
      flexWrap={"nowrap"}
      direction={"column"}
      alignItems={"center"}
      justifyContent={"center"}
      width="100%"
      height={size({ p: 60, h: true })}
      rowGap={size({ p: 2.5 })}
      mb={size({ p: 5, h: true })}
      mt={size({ p: 5, h: true })}
      className={styles.container}
      id="ra_reach_us"
    >
      {/* Title */}
      <Grid
        item
        container
        flexWrap={"nowrap"}
        alignItems={"center"}
        justifyContent={"center"}
        columnGap={size({ p: 2 })}
      >
        <Grid item>
          <Typography
            sx={{
              fontFamily: "Archia",
              fontSize: size({ p: 1.8 }),
              fontWeight: 800,
              color: COLORS.C2,
              textAlign: "center",
            }}
            className={styles.reachtitle}
          >
            Let’s Discuss Your Technical Needs
          </Typography>

          <Typography
            sx={{
              fontFamily: "Space Mono",
              fontSize: size({ p: 1 }),
              fontWeight: 400,
              color: `${COLORS.C2}`,
              textAlign: "center",
              marginTop: size({ p: 0.8, h: true }),
            }}
            className={styles.reachsubtitle}
          >
            Share your details with us — we’ll take it from there.
          </Typography>
        </Grid>
      </Grid>

      {/* Form rows */}
      <Grid
        item
        container
        flexWrap={"nowrap"}
        columnGap={size({ p: 2 })}
        padding={`0px ${size({ p: 20 })}`}
        className={styles.formrow}
      >
        {/* Column 1 */}
        <Grid
          item
          container
          direction={"column"}
          rowGap={size({ p: 1.5, h: true })}
          className={styles.formcolumn}
        >
          <TextField
            tf_props={{
              placeholder: "First Name",
              value: formData.firstName,
              onChange: (e) =>
                setFormData({ ...formData, firstName: e.target.value }),
            }}
          />

          <TextField
            tf_props={{
              placeholder: "Email Address",
              value: formData.email,
              onChange: (e) =>
                setFormData({ ...formData, email: e.target.value }),
            }}
          />

          <Autocomplete
            options={SERVICES}
            value={formData.service ? { label: formData.service } : null}
            onChange={(event, newValue) => {
              setFormData({
                ...formData,
                service: newValue ? newValue.label : "",
              });
            }}
            renderInput={(params) => (
              <TextField params={params} tf_props={{ placeholder: "Service" }} />
            )}
          />
        </Grid>

        {/* Column 2 */}
        <Grid
          item
          container
          direction={"column"}
          rowGap={size({ p: 1.5, h: true })}
          className={styles.formcolumn}
        >
          <TextField
            tf_props={{
              placeholder: "Last Name",
              value: formData.lastName,
              onChange: (e) =>
                setFormData({ ...formData, lastName: e.target.value }),
            }}
          />

          <TextField
            tf_props={{
              placeholder: "Contact Number",
              value: formData.contact,
              onChange: (e) =>
                setFormData({ ...formData, contact: e.target.value }),
            }}
          />

          <TextField
            tf_props={{
              placeholder: "Your Subject Area",
              value: formData.subject,
              onChange: (e) =>
                setFormData({ ...formData, subject: e.target.value }),
            }}
          />
        </Grid>
      </Grid>

      {/* Message Box */}
      <Grid
        item
        container
        alignItems={"center"}
        justifyContent={"center"}
        padding={`0px ${size({ p: 20 })}`}
        className={styles.fullwidthinput1}
      >
        <Grid item width={size({ p: 60 })} className={styles.desc}>
          <TextField
            tf_props={{
              placeholder: "Share Your Thoughts With Us",
              value: formData.message,
              onChange: (e) =>
                setFormData({ ...formData, message: e.target.value }),
              multiline: true,
              rows: 5,
            }}
          />
        </Grid>
      </Grid>

      {/* Submit Button */}
      <Grid
        item
        container
        direction={"column"}
        padding={`0px ${size({ p: 35 })}`}
        rowGap={size({ p: 0.5, h: true })}
      >
        <Button
          variant="contained"
          fullWidth
          onClick={handleSubmit}
          disabled={isSubmitting}
          sx={{
            backgroundColor: `${COLORS.C2}`,
            fontFamily: "Space Mono",
            fontWeight: 400,
            fontSize: size({ p: 1 }),
            color: `${COLORS.WHITE}`,
            textTransform: "none",
            display: "flex",
            justifyContent: "center",
          }}
          className={styles.btn}
        >
          {isSubmitting ? (
            <CircularProgress size={22} color="inherit" />
          ) : (
            "Submit"
          )}
        </Button>
      </Grid>
    </Grid>
  );
}
